import {
  FieldMetadataForTypes as FieldMetadata,
  IssueStandardFieldIds,
} from "@adsk/acc-issues-types";
import {
  DEFAULT_FIELD_ORDER,
  DEFAULT_BOTTOM_FIELDS,
} from "./issueStandardFields";

export const getDefaultStandardFieldMetadata = (
  fieldId: IssueStandardFieldIds
): FieldMetadata => {
  let defaultVisibility = true;

  if (fieldId === IssueStandardFieldIds.GpsCoordinates) {
    defaultVisibility = false;
  }

  return {
    type: "standard",
    id: fieldId,
    visible: defaultVisibility,
  };
};

export const getDefaultCustomFieldMetadata = (
  customFieldId: string
): FieldMetadata => {
  return {
    type: "custom",
    id: customFieldId,
    visible: true,
  };
};

export const getSanitizedFieldList = ({
  fieldList,
  customFieldIdsForType,
}: {
  fieldList: FieldMetadata[];
  customFieldIdsForType: string[];
}) => {
  const customFieldIdsForTypeAsSet = new Set(customFieldIdsForType);

  return fieldList.filter((field: FieldMetadata) => {
    if (!field) {
      return false;
    }
    // filter out any weird data that has an invalid type
    if (!["standard", "custom"].includes(field.type)) {
      return false;
    }
    // filter out any weird standard field data
    if (
      field.type === "standard" &&
      !DEFAULT_FIELD_ORDER.includes(field.id as IssueStandardFieldIds)
    ) {
      return false;
    }
    // filter out any custom fields that do not currently belong to the type
    if (field.type === "custom" && !customFieldIdsForTypeAsSet.has(field.id)) {
      return false;
    }
    return true;
  });
};

/**
 * we need to add to the field list any custom fields,
 * and we need to make sure that the fixed fields are always at the top.
 */
export const useGetSanitizedFieldOrder = ({
  fieldList,
  customFieldIdsForType,
  shouldShowAttachments = false,
}: {
  fieldList: FieldMetadata[];
  customFieldIdsForType: string[];
  shouldShowAttachments?: boolean;
}): FieldMetadata[] => {
  let fieldListCopy = getSanitizedFieldList({
    fieldList,
    customFieldIdsForType,
  });

  // Add all of the standard fields to the beginning, in order,
  // if they are not already included in the metadata.
  const standardFieldIdsAlreadyInMetadata = new Set(
    fieldListCopy
      .filter((field: FieldMetadata) => {
        return field.type === "standard";
      })
      .map((field: FieldMetadata) => field.id)
  );
  // for the default order, we need a reversed version so we can iterate through it and push items into the front:
  // this gives us the correct order
  const reversedStandardFieldDefaultOrder = [...DEFAULT_FIELD_ORDER];
  reversedStandardFieldDefaultOrder.reverse();

  reversedStandardFieldDefaultOrder.forEach(
    (standardFieldId: IssueStandardFieldIds) => {
      if (!standardFieldIdsAlreadyInMetadata.has(standardFieldId)) {
        fieldListCopy.unshift(getDefaultStandardFieldMetadata(standardFieldId));
      }
    }
  );

  // we add any custom fields not included in the existing fields metadata
  const customFieldIdsAlreadyInMetadata = new Set(
    fieldListCopy
      .filter((field: FieldMetadata) => {
        return field.type === "custom";
      })
      .map((field: FieldMetadata) => field.id)
  );

  // if some of the default bottom fields are at the bottom, the custom fields not specified in the metadata
  // should be above them. Otherwise we add the custom fields in their specified place.
  const lastDefaultFields = [...DEFAULT_BOTTOM_FIELDS].reverse();
  if (!shouldShowAttachments) {
    const attachmentIndex = lastDefaultFields.indexOf(
      IssueStandardFieldIds.Attachments
    );
    lastDefaultFields.splice(attachmentIndex, 1);
  }

  // count how many of the last default fields are in their default place
  const lastDefaultFieldsInDefaultPlaceCount = lastDefaultFields.reduce(
    (acc, fieldId, index) => {
      return fieldListCopy[fieldListCopy.length - (index + 1)].id === fieldId
        ? acc + 1
        : acc;
    },
    0
  );

  customFieldIdsForType.forEach((customFieldId: string) => {
    if (!customFieldIdsAlreadyInMetadata.has(customFieldId)) {
      fieldListCopy.splice(
        lastDefaultFieldsInDefaultPlaceCount
          ? //use negative index to insert the custom field above the first bottom default field
            -1 * lastDefaultFieldsInDefaultPlaceCount
          : fieldListCopy.length,
        0,
        getDefaultCustomFieldMetadata(customFieldId)
      );
    }
  });

  // set the required field to false by default if it is not specified
  fieldListCopy = fieldListCopy.map((field: FieldMetadata) => ({
    required: false,
    ...field,
  }));

  // if attachments are not shown, remove the attachments field
  if (!shouldShowAttachments) {
    fieldListCopy = fieldListCopy.filter((field: FieldMetadata) => {
      return field.id !== IssueStandardFieldIds.Attachments;
    });
  }

  return fieldListCopy;
};
